import React, { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Spaces from '@availity/spaces';
import { useUpdateNav } from '@availity/hooks';
import { usePendo } from '@av/pendo';
import qs from 'query-string';

import { StoreProvider } from './stores';
import { HomePage, ApiDocsPage, Navigation, Footer, SessionWarningModal } from './components';
import JoinEarlyPage from './components/JoinEarlyPage';
import { useSessionStore, useScrollRestoration } from './hooks';
import { initializeSession, type SessionStore } from './auth/sessionService';
import { initializeForgeRock } from './auth/authUtils';
import ProtectedRoute from './auth/ProtectedRoute';

const RouteWrapper = () => {
  useUpdateNav();
  return <Outlet />;
};

const NAV_ROUTES: Record<string, string> = {
  '/': 'Home',
  '/docs/overview': 'Docs_Overview',
  '/docs/api-spec': 'API_Spec',
  '/docs/mcp': 'MCP',
  '/access-hub': 'Access_Hub',
  '/join-early': 'Join_Early',
};

const pushNavDetailsToPendo = (currentPath: string) => {
  const pendo = window.pendo as unknown as { track?: (event: string, properties?: Record<string, unknown>) => void } | undefined;
  const navigatedFrom = NAV_ROUTES[currentPath];
  if (pendo?.track && navigatedFrom) {
    pendo.track('Navigation to Developer Portal', { Navigated_From_String: navigatedFrom });
  }
};

const AppContent = ({ spaceId }: { spaceId: string }) => {
  const sessionStore = useSessionStore();
  useScrollRestoration();
  usePendo((sessionStore as SessionStore)?.user?.akaname as string ?? '');

  useEffect(() => {
    const checkPendoReady = setInterval(() => {
      if (window.pendo) {
        clearInterval(checkPendoReady);
        try {
          const currentPath = window.location.pathname;
          pushNavDetailsToPendo(currentPath);
          // eslint-disable-next-line no-empty
        } catch {}
      }
    }, 100);

    const timeoutId = setTimeout(() => {
      clearInterval(checkPendoReady);
    }, 25000);

    return () => {
      clearInterval(checkPendoReady);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    initializeForgeRock();
    if (!sessionStore) return;
    // eslint-disable-next-line consistent-return
    return initializeSession(sessionStore as SessionStore);
  }, [sessionStore]);

  return (
    <Spaces spaceIds={[spaceId]} clientId="test">
      <div data-testid="app-container" id="app-container" className="w-100">
        <Navigation />
        <Routes>
          <Route element={<RouteWrapper />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/docs" element={<Navigate to="/docs/overview" replace />} />
            <Route path="/docs/overview" element={<ApiDocsPage />} />
            <Route path="/docs/api-spec" element={<ApiDocsPage />} />
            <Route path="/docs/mcp" element={<ApiDocsPage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/access-hub" element={<ApiDocsPage />} />
            </Route>
            <Route path="/join-early" element={<JoinEarlyPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
        <SessionWarningModal />
        <Footer />
      </div>
    </Spaces>
  );
};

const App = (): JSX.Element => {
  const { search } = useLocation();
  const spaceId = (qs.parse(search).spaceId as string) || 'default';

  return (
    <StoreProvider>
      <AppContent spaceId={spaceId} />
    </StoreProvider>
  );
};

export default App;